import {
  CACHE_HEADER_ENABLED,
  CACHE_HEADER_RELOAD,
  CACHE_HEADER_STALE_TIME,
} from "@sinch/cache-helpers";
import { RequestCacheClient } from "@sinch/cache-helpers/lib/cache";

const cacheLayerHeaders = [
  CACHE_HEADER_ENABLED,
  CACHE_HEADER_STALE_TIME,
  CACHE_HEADER_RELOAD,
];
type CacheLayerHeaderKey = (typeof cacheLayerHeaders)[number];
type CacheOptions = Partial<Record<CacheLayerHeaderKey, string>>;

const retrieveOptionFromHeaders = (
  request: Request
): [Request, CacheOptions] => {
  const options = cacheLayerHeaders.reduce<CacheOptions>((acc, key) => {
    if (request.headers.has(key)) {
      const value = request.headers.get(key);
      request.headers.delete(key);
      return { ...acc, [key]: value };
    }
    return acc;
  }, {});

  return [new Request(request), options];
};

const w = window as Window & typeof globalThis & { rcc?: RequestCacheClient };
function getRequestCacheClient() {
  if (!w.rcc) {
    w.rcc = new RequestCacheClient();
  }
  return w.rcc;
}

export const patchFetchWithCacheLayer = () => {
  //////////////////////////////////////////////////////////////////////////////
  // Patch fetch
  const { fetch: originalFetch } = window;
  window.fetch = function fetchWithCache(
    ...args: Parameters<typeof window.fetch>
  ): ReturnType<typeof window.fetch> {
    const requestCacheClient = getRequestCacheClient();
    const [patchedRequest, cacheOptions] = retrieveOptionFromHeaders(
      new Request(...args)
    );

    if (cacheOptions[CACHE_HEADER_ENABLED] === "true") {
      const entry = requestCacheClient.getEntry(new URL(patchedRequest.url));
      const reloadCacheOption = cacheOptions[CACHE_HEADER_RELOAD];
      const staleTimeOption = cacheOptions[CACHE_HEADER_STALE_TIME];
      const staleTime = staleTimeOption ? parseInt(staleTimeOption) : undefined;

      if (entry && !entry.isStale(staleTime) && reloadCacheOption !== "true") {
        return entry.value;
      }

      const promisedResponse = originalFetch(patchedRequest);

      requestCacheClient.createEntry(
        new URL(patchedRequest.url),
        promisedResponse
      );

      return promisedResponse.then((res) => res.clone());
    }

    return originalFetch(patchedRequest);
  };

  // reset to original
  return () => {
    window.fetch = originalFetch;
  };
};
